import { useEffect, useState } from "react";

/**
 * Client-side watchlist persisted to localStorage. Modelled on the approach
 * used in flaunch-scanner: no auth, no server round-trip, just a list of
 * keys kept in the browser. This avoids the magic-link-email friction that
 * was blocking the POST /api/watchlist path.
 *
 * Entries are stored with enough metadata to render the watchlist page
 * without another fetch — each one is self-describing.
 */

export interface WatchlistEntry {
  /** Stable key used for dedupe. `${kind}:${chain}:${address}:${tokenId}`. */
  key: string;
  kind: "CARD" | "PACK";
  chain: "base" | "apechain";
  chainId: number;
  /** Contract address — slab ERC-721 for cards, SlabMachine for packs. */
  address: string;
  tokenId: string;
  /** Display metadata — optional but keeps the list page snappy. */
  description?: string;
  imageUrl?: string;
  rarity?: string;
  valueUsd?: number;
  addedAt: number;
}

const STORAGE_KEY = "slabscanner:watchlist:v1";
const EVENT_NAME = "slabscanner:watchlist:updated";

function loadFromStorage(): WatchlistEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is WatchlistEntry =>
        typeof e === "object" &&
        e !== null &&
        typeof (e as WatchlistEntry).key === "string" &&
        typeof (e as WatchlistEntry).address === "string"
    );
  } catch {
    return [];
  }
}

function saveToStorage(entries: WatchlistEntry[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    // Fan out to other hook instances in this tab so every component sees
    // the update immediately (storage events don't fire on the origin tab).
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch {
    // Quota exceeded / disabled storage — fail silently.
  }
}

/** Canonical key for a watchlist entry. Lowercases address for consistency. */
export function makeWatchlistKey(params: {
  kind: "CARD" | "PACK";
  chain: "base" | "apechain";
  address: string;
  tokenId: string;
}): string {
  return `${params.kind}:${params.chain}:${params.address.toLowerCase()}:${params.tokenId}`;
}

export function useWatchlist() {
  const [entries, setEntries] = useState<WatchlistEntry[]>(() => loadFromStorage());

  useEffect(() => {
    const onUpdate = () => setEntries(loadFromStorage());
    // Cross-tab sync.
    window.addEventListener("storage", onUpdate);
    // Same-tab sync between hook instances.
    window.addEventListener(EVENT_NAME, onUpdate);
    return () => {
      window.removeEventListener("storage", onUpdate);
      window.removeEventListener(EVENT_NAME, onUpdate);
    };
  }, []);

  function add(entry: Omit<WatchlistEntry, "key" | "addedAt"> & { key?: string }) {
    const key =
      entry.key ??
      makeWatchlistKey({
        kind: entry.kind,
        chain: entry.chain,
        address: entry.address,
        tokenId: entry.tokenId,
      });
    const current = loadFromStorage();
    if (current.some((e) => e.key === key)) return;
    const next: WatchlistEntry[] = [
      ...current,
      { ...entry, key, addedAt: Date.now() },
    ];
    saveToStorage(next);
    setEntries(next);
  }

  function remove(key: string) {
    const next = loadFromStorage().filter((e) => e.key !== key);
    saveToStorage(next);
    setEntries(next);
  }

  function isWatched(key: string): boolean {
    return entries.some((e) => e.key === key);
  }

  return { entries, add, remove, isWatched };
}
